module.exports={render:function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _vm._m(0)
},staticRenderFns: [function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _c('div', {
    staticClass: "chapter"
  }, [_c('div', {
    staticClass: "inner_page"
  }, [_c('div', {
    attrs: {
      "id": "chapter02"
    }
  }, [_c('p', {
    staticClass: "chap-num"
  }, [_c('a', {
    attrs: {
      "href": "#/Table_of_contents"
    }
  }, [_vm._v("2")])]), _vm._v(" "), _c('p', {
    staticClass: "chap-ttl"
  }, [_c('a', {
    attrs: {
      "href": "#/Table_of_contents"
    }
  }, [_c('span', {
    staticClass: "epub-bold-normal-normal"
  }, [_vm._v("Strategies of interpretation: three essential strategies for Romans")])])]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm-first"
  }, [_vm._v("From among varied strategies of interpretation, three are essential for understanding Paul’s argument: historical-critical methods, rhetorical criticism and socio-scientific criticism. Seven others, which include reader-response theory, liberation hermeneutics, structuralism and existential interpretation, vary in importance from one section of the epistle to another, but all nevertheless remain very useful. It is therefore appropriate to divide what was a single chapter on strategies of interpretation into two, especially since including the first three would create a chapter of more than normal length.")]), _vm._v(" "), _c('p', {
    staticClass: "h1",
    attrs: {
      "id": "a1"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/Table_of_contents"
    }
  }, [_c('span', {
    staticClass: "epub-semibold-normal-normal"
  }, [_vm._v("Historical-critical methods")])])]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm-first"
  }, [_vm._v("In spite of the fact that so many speak of "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("the")]), _vm._v(" historical-critical method, in practice "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("many")]), _vm._v(" such methods abound. To use "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("the singular ‘method’")]), _vm._v(" is a misleading mistake. There is no such thing as ‘the’ critical method, even if Karl Donfried in "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("The Romans Debate")]), _vm._v(" speaks of ‘the’ historical-critical method."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n1",
      "id": "f1"
    }
  }, [_vm._v("1")])]), _vm._v(" Initially the term denoted an approach mainly through historical reconstruction "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("alone")]), _vm._v(", together with what many regard as ‘the assured results of biblical criticism’. The initial method carried a strongly anti-theological dimension, as if theology came only from church tradition.")]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm"
  }, [_vm._v("Further, it is also misleading to speak of these methods as a ‘modern’ phenomenon. Historical-critical approaches emerged not only in the seventeenth century, but also in anticipatory and fragmented traces in Origen, Luther and other pre-modern figures. The eighteenth and nineteenth centuries (though with strong exceptions) are more indebted to a ‘history versus theology’ mentality than approaches during the post-war years of the later twentieth century, and even more so today. We must therefore outline how historical-critical methods have developed and >been modified.")]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm"
  }, [_vm._v("Some trace proto-critical methods back to Benedict Spinoza the Jewish philosopher (1632–77),to Richard Simon the Catholic Jesuit (1638–1712), to Jean Astruc (1684–1766) and to the Deists. But these were only partial anticipations of a more systematically critical approach. Spinoza identified and criticized biblical ‘contradictions’. Simon challenged the assumption that Moses wrote the Pentateuch, in a quest to undermine Protestant appeals to the biblical writings. Astruc postulated that two sources lay behind Genesis and Exodus, largely on the basis of their use of two different divine names, Jahweh or Yahweh (the ‘J’sources), and Elohim, God (the E’sources). This occurred long before Wellhausen popularized the same idea.")]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm"
  }, [_vm._v("A more systematic approach came with the work of J. S. Semler, who published his "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Treatise on the Free Investigation of the Canon")]), _vm._v(" (1771–5), in which he argued that reason or rational enquiry should not be ‘hindered’ by any appeal to faith or to Christian theology. The tragic separation between theology and strictly historical versions of biblical criticism can be traced to his originally positive motives for separating rational and historical study from faith and theology.")]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm"
  }, [_vm._v("During the eighteenth century this approach became institutionalized through the work of J. D. Michaelis (1788), J. J. Griesbach (1776), G. E. Lessing (1777) and W. M. L. de Wette (1788–1849). De Wette, for example, stressed different outlooks in Kings and Chronicles, arguing that Chronicles was a secondary historical source. In the nineteenth century, David Strauss (1808–74) extended such historical criticism to the Gospels. Others began to include the epistles, including F. C. Baur (1792–1860), whom we discuss under our Chapter 4, ‘The reception history of Romans’.")]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm"
  }, [_vm._v("In the early years of the twentieth century Paul Wendland (1907) and Rudolf Bultmann (1910) proposed analogies between Greek literature and Paul’s style in Romans (see below under‘Rhetorical criticism’). Wilhelm Wrede (1904) and Albert Schweitzer (1930) denied the centrality in Romans of justification by grace through faith. They were building largely on H. Lüdemann’s earlier theory (1872) that Paul held two quite different doctrines of redemption. One centred on the juridical theme of Romans 1—4; and the other on the‘mystical’theme of dying-and-rising with Christ, found in Romans 5—8. The former derived largely from Judaism; the latter was allegedly borrowed from Greek thought or Hellenism.")]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm"
  }, [_vm._v("This seemed to cohere with advocates of the history-of-religions school of W. Bousset, R. Reitzenstein and P. Wendland. Bousset’s "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Kyrios Christos")]), _vm._v(" (German 1913, English 1970) was endorsed by Bultmann. Like Harnack, the classical liberal, Bousset contrasted the simple ethical demands of Jesus with Paul’s more complex theology, which allegedly draws on the Hellenistic mystery religions. Reitzenstein (1861–1931) was primarily a classical philologist, who sought to understand what he called ‘the Redeemer-myth’ of Gnosticism. He argued that Paul derived some aspects from the Gnostic myth of a saviour figure, who came down from heaven to rescue spirits imprisoned on earth.")]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm"
  }, [_vm._v("The sheer range of historical-critical methods can be illustrated further by selecting two reactions to the theories of Reitzenstein. Günther Wagner carefully examines Paul’s view, not least in Romans 6.1–11, and compares the myths or‘mysteries’of Isis, Mithras and other ancient deities."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n2",
      "id": "f2"
    }
  }, [_vm._v("2")])]), _vm._v(" His arguments were that, first, if there is any parallel with Paul, this does not at all mean that Paul borrowed from them. Second, the dating of such sources indicates that they often post-date Paul, so could not have influenced him. Third, supposed ‘parallels’ do not turn out to be parallels at all. Similarly Munck convincingly demolishes the hypotheses of Baur. We discuss his work under‘Reception history’,our Chapter 4, below.")]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm"
  }, [_vm._v("T. W. Manson was Professor of Biblical Criticism and Exegesis at Manchester University for some 20 years from 1936, and again represents a different type of critical approach. We discuss his theory about Romans in our Chapter 5 on the text and integrity of Romans.")]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm"
  }, [_vm._v("After the Second World War, historical-critical studies often provided a deeper understanding of Judaism. In the post-war era W. D. Davies argued that Paul’s thought was best understood against the background of‘Palestinian’Judaism and the new exodus. He rescued Paul from a one-sided emphasis on the Greek background. In Romans the apostle illustrates affinities with, rather than opposition to, Jewish rabbis. For example, in his chapter on ‘the flesh and sin’, Davies examined not only these terms in Romans, but also the rabbinic notion of "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("he")]), _vm._v("-"), _c('span', {
    staticClass: "italics"
  }, [_vm._v("yêtzer hâ-ra‘")]), _vm._v(", or evil impulse."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n3",
      "id": "f3"
    }
  }, [_vm._v("3")])]), _vm._v(" This may well stand behind Paul’s language about the ineffectiveness of the law alone. There are also links, he adds, with the fall of Adam in Romans 5.12–21."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n4",
      "id": "f4"
    }
  }, [_vm._v("4")])]), _vm._v(" He comments on Romans 5.12: ‘Paul accepted the traditional rabbinic doctrine of the unity of mankind in Adam.’"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n5",
      "id": "f5"
    }
  }, [_vm._v("5")])])]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm"
  }, [_vm._v("Further Jewish approaches to Paul were then discussed and represented by H. J. Schoeps. Stendahl also offered a new approach, and we discuss his work under ‘Reception history’.In 1977 E. P. Sanders published his monumental book, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Paul and Palestinian Judaism")]), _vm._v(", which aimed to draw a more careful comparison between Paul and Judaism than had yet appeared. For many, but not for all, this constituted a watershed in the study of Paul, introducing what came to be called ‘The New Perspective’. We discuss Sanders, again, under our Chapter 4, on reception history.")]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm"
  }, [_vm._v("James Dunn (b. 1939) contributed a positive but modified assessment of Sanders’‘New Perspective’ in 1991. He conceded that Sanders built on earlier approaches of Stendahl and N. A. Dahl and confirmed their general point: ‘Pauline exegesis has far too long allowed a typically Lutheran emphasis on justification by faith to impose a hermeneutical grid on the text of Romans.’"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n6",
      "id": "f6"
    }
  }, [_vm._v("6")])]), _vm._v(" This suggested that first-century Judaism presented a system in which salvation is ‘earned’ through the merit of good works. Dunn explains:‘The role of the law … provides a crucial hinge in the argument (Rom. 3:19–21; 8:2–4; 9:31–10.5).’"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n7",
      "id": "f7"
    }
  }, [_vm._v("7")])]), _vm._v(" He criticized Sanders, however, for understanding the justification theme and that of Christ-Union as "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("contradictory")]), _vm._v(" lines of thought, as Schweitzer did.")]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm"
  }, [_vm._v("Dunn insists,‘The law … became a basic expression of Israel’s distinctiveness as the people specially chosen by (the one) God to be his people.’ In sociological terms the law became an ‘identity-marker’ and ‘boundary’, reinforcing Israel’s sense of distinctiveness."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n8",
      "id": "f8"
    }
  }, [_vm._v("8")])]), _vm._v(" Thus Israel’s revelling in the law as a badge of God’s favour ran counter to Christian Jews welcoming the Gentiles as their equals. The law, Dunn concludes, was what divided Jew from non-Jew. He adds, ‘Paul regularly warns against“the works of the law”not as “good works” … but rather as that pattern of obedience by which “the righteous” maintain their status within the people of the covenant.’"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n9",
      "id": "f9"
    }
  }, [_vm._v("9")])])]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm"
  }, [_vm._v("N. T. Wright (b. 1948) developed a further modification of the New Perspective in 2005."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n10",
      "id": "f10"
    }
  }, [_vm._v("10")])]), _vm._v(" He regards Romans as entailing ‘the redefinition of God’s people around the Messiah’."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n11",
      "id": "f11"
    }
  }, [_vm._v("11")])]), _vm._v(" On Romans he insists: ‘Rom. 2:17–25 examines the Jewish claim to special status, based on election and the covenant.’"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n12",
      "id": "f12"
    }
  }, [_vm._v("12")])]), _vm._v(" Wright, like Dunn, regards this as corporate and national, not as individual. The presence of sin, and boasting in the Torah, deceives Israel into thinking that God will achieve his purposes through that nation, rather than through Christ. It is more accurate to say that the ‘alternative’ to law is not ‘abandoning good works’, but "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Christ")]), _vm._v(". Wright also notes elsewhere that Alister McGrath recognized that the doctrine of justification ‘has come to develop a meaning quite independent of its biblical origins, and concerns the means by which men’s relationship to God is established … thereby giving the concept an emphasis quite absent from the New Testament’."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n13",
      "id": "f13"
    }
  }, [_vm._v("13")])])]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm"
  }, [_vm._v("Meanwhile C. E. B. Cranfield, Seyoon Kim and many others firmly "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("resist")]), _vm._v(" the New Perspective. In an oral interview with Ben Blackwell Cranfield told him that he remained unconvinced by the New Perspective, and thought that Dunn’s commentary was ‘off target’ in this area."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n14",
      "id": "f14"
    }
  }, [_vm._v("14")])]), _vm._v(" As we have noted, Seyoon Kim refuses to ‘elevate it into a dogma’. On the other hand many allow for its broad recasting of Jewish–Gentile relations in Romans, without following its claims in detail. Notably the two most recent major and classic commentaries on Romans, those by Fitzmyer and Jewett, appear to pay relatively little attention to the New Perspective as such.")]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm"
  }, [_vm._v("In conclusion it is clear that ‘the historical-critical method’ is "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("no single approach")]), _vm._v(", but embraces many methods, and no longer carries the ‘history versus theology’ slogan of earlier years. Its more positive attitude to "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("theology")]), _vm._v(" can be seen from the last five or six major commentaries on Romans. Barrett (1957), Dunn (1988), Ziesler (1989) and Jewett (2007) are Methodists; Fitzmyer (1992) and Byrne (1996) remain convinced Catholics; Cranfield (1977) is a committed member of the Reformed Church; Nygren and Käsemann (1973) are Lutherans; Wright (2002) is an Anglican, and became Bishop of Durham (like Westcott and Lightfoot); while Bruce belonged to the Christian Brethren. Jewett states concerning his magisterial commentary:")]), _vm._v(" "), _c('p', {
    staticClass: "ext-only"
  }, [_vm._v("This commentary employs all the standard methods of historical-critical exegesis. This includes historical analysis, text-criticism, form criticism and redaction criticism; rhetorical analysis, social scientific reconstruction of the audience situation; an historical and cultural analysis of the honour-shame, and imperial systems in the Graeco-Roman world, and a "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("theological interpretation that takes these details into account")]), _vm._v(", rather than following paths formed by church traditions."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n15",
      "id": "f15"
    }
  }, [_vm._v("15")])])]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm-first"
  }, [_vm._v("His commentary powerfully and convincingly deploys all the different methods which he has cited under the heading ‘historical-critical exegesis’.")]), _vm._v(" "), _c('p', {
    staticClass: "h1",
    attrs: {
      "id": "a2"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/Table_of_contents"
    }
  }, [_c('span', {
    staticClass: "epub-semibold-normal-normal"
  }, [_vm._v("Rhetorical criticism")])])]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm-first"
  }, [_vm._v("In one sense rhetorical criticism is as old as the analysis of the literary artistry of the Old Testament (OT), and as handbooks of rhetoric in ancient Greece and Rome. Duane Watson cites the rhetorical style of Amos 1—2 and the Song of Deborah in Judges 5."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n16",
      "id": "f16"
    }
  }, [_vm._v("16")])]), _vm._v(" He also cites such literary studies as Michael Fishbane’s "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Text and Texture")]), _vm._v(", Robert Alter’s "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("The Art of Biblical Narrative")]), _vm._v(" and Northrop Fry’s "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("The Great Code")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm"
  }, [_vm._v("We are concerned, however, with Romans and Paul. In this respect one of the earliest modern studies to draw heavily on Greek and Roman rhetoric was Rudolf Bultmann’s thesis in 1910."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n17",
      "id": "f17"
    }
  }, [_vm._v("17")])]), _vm._v(" He refers to G. F. G. Heinrici’s work of 1908 on the literary character of the New Testament (NT) writings, and to some earlier works, including those of Wilke (1843) and Paul Wendland (1895). But here he scrutinizes specifically the diatribe in Graeco-Roman authors. These include Bion, Teles, Horace, Musonius Rufus, Dion, and above all Epictetus and Plutarch."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n18",
      "id": "f18"
    }
  }, [_vm._v("18")])]), _vm._v(" Plutarch ("), _c('span', {
    staticClass: "italics"
  }, [_vm._v("c.")]), _vm._v("46–120) and Epictetus ("), _c('span', {
    staticClass: "italics"
  }, [_vm._v("c.")]), _vm._v("50 – "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("c.")]), _vm._v("130) were almost contemporary with Paul. Lucius Annaeus Seneca ("), _c('span', {
    staticClass: "italics"
  }, [_vm._v("c.")]), _vm._v("1–65) overlapped with Paul. These provided examples of Stoic diatribe and of the popular ethical preaching of the Cynics."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n19",
      "id": "f19"
    }
  }, [_vm._v("19")])])]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm"
  }, [_vm._v("Bultmann examined especially the dialogical character of the Stoics’ diatribe, their rhetorical style, their groupings and style, and their distinctive manner of argumentation. He argued that Paul includes all these features. In Paul he selects several examples of the diatribe style. These include rhetorical questions (Rom. 6.16, ‘Do you not know that … ?’, and similarly Rom. 11.2; 7.1; cf. 1 Cor. 3.18; 5.6; 6.15; 9.15)."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n20",
      "id": "f20"
    }
  }, [_vm._v("20")])]), _vm._v(" Paul addresses both single people and groups as rhetorical opponents or dialogue-partners. Often he addresses questions about consequences.")]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm"
  }, [_vm._v("Epictetus practises exactly the same style. He addresses single identities, e.g. "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("anthrōpe")]), _vm._v(", ‘O human person’ (cf. Rom. 1.13). Often Paul presses hypothetical arguments which he rejects with such emphatic negatives as ‘By no means!’ (NRSV); Greek "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("mē genoito")]), _vm._v(" (Rom. 3.4; 3.31)."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n21",
      "id": "f21"
    }
  }, [_vm._v("21")])]), _vm._v(" In Romans 9.19 Paul declares, ‘You will say to me then …’ Romans 2.21 borrows Diaspora Jewish rhetoric, to which Paul replies: ‘You, then, that teach others, will you not teach yourself?’"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n22",
      "id": "f22"
    }
  }, [_vm._v("22")])]), _vm._v(" Bultmann also includes as evidence the ‘catalogue of vices’ which we find in Romans 1.24–31. With these lists we may compare Epictetus, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Discourses")]), _vm._v(" 1.18, 22 and 1.11, 33."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n23",
      "id": "f23"
    }
  }, [_vm._v("23")])]), _vm._v(" Opposition on the part of a fictitious opponent is sharpened not only in Paul, but also in Teles 15.14; 26.8; Epictetus, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Dis.")]), _vm._v(" 1.2, 22; 14.11; Plutarch, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("De Tranqu. Animae")]), _vm._v(" 469E; Seneca, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("De Const. Sap.")]), _vm._v(" 1.2. Fictitious conversations are prominent (Epictetus, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Dis.")]), _vm._v(" 1.4, 5; 18.17; 19.2; 24.9).")]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm"
  }, [_vm._v("Bultmann also includes in this rhetorical diatribe style word-play, antithesis, parallelism, paradox, reversal of values, and exclamations."), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Discourses")]), _vm._v(" 1.18, 22 and 1.11, 33."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n24",
      "id": "f24"
    }
  }, [_vm._v("24")])]), _vm._v(" Word-play is difficult to illustrate in English, but we refer to examples in Greek in our commentary (Rom. 1.25, 27, 28; 2.8; 5.16, 19). Bultmann concludes, for example, ‘A certain similarity between Paul and the diatribe comes to light.’"), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Discourses")]), _vm._v(" 1.18, 22 and 1.11, 33."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n25",
      "id": "f25"
    }
  }, [_vm._v("25")])])]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm"
  }, [_vm._v("For many years Bultmann had established Paul’s use of the diatribe. But his approach undermined A. Deissmann’s later claims about spontaneous, artless, ‘letters’. He established that Paul deliberately involved and engaged his audience, relying especially on conversational style. He concluded:‘The cloak of the Greek orator does indeed hang on Paul’s shoulders, but Paul has no sense for skilful drapery.’"), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Discourses")]), _vm._v(" 1.18, 22 and 1.11, 33."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n26",
      "id": "f26"
    }
  }, [_vm._v("26")])]), _vm._v(" But the most decisive work came from Stanley Stowers in 1981."), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Discourses")]), _vm._v(" 1.18, 22 and 1.11, 33."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n27",
      "id": "f27"
    }
  }, [_vm._v("27")])]), _vm._v(" He pointed out that Ernst von Dobschütz had identified question-and-answer methods and styles in the "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Jewish rabbis")]), _vm._v(", in the Midrash and in the Talmud."), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Discourses")]), _vm._v(" 1.18, 22 and 1.11, 33."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n28",
      "id": "f28"
    }
  }, [_vm._v("28")])]), _vm._v(" Similarly in 1972 Edwin Judge had argued that NT scholars had worked on the Cynic–Stoic diatribe with insufficient critical awareness."), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Discourses")]), _vm._v(" 1.18, 22 and 1.11, 33."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n29",
      "id": "f29"
    }
  }, [_vm._v("29")])])]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm"
  }, [_vm._v("Stowers recognizes that ‘there is a strong dialogue element’ in Teles, Epictetus and Paul."), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Discourses")]), _vm._v(" 1.18, 22 and 1.11, 33."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n30",
      "id": "f30"
    }
  }, [_vm._v("30")])]), _vm._v(" Bultmann’s conclusions, he argues, remain ambiguous, but do underline the role of rhetoric. In this respect he agrees that ‘The function of Rom. 2:1–5 is to bring home, to concretize and to sharpen the indictment in 1:18–32.’"), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Discourses")]), _vm._v(" 1.18, 22 and 1.11, 33."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n31",
      "id": "f31"
    }
  }, [_vm._v("31")])]), _vm._v(" In particular Paul uses rhetoric to characterize those who are ‘arrogant’ or ‘pretentious’. Stowers compares Romans 2.17–24 with the Stoic indictment of the arrogant."), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Discourses")]), _vm._v(" 1.18, 22 and 1.11, 33."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n32",
      "id": "f32"
    }
  }, [_vm._v("32")])]), _vm._v(" Passages with similar traits are Romans 9.19–21 and 11.17–24. In 11.13 he says, ‘Now I am speaking to you Gentiles.’ Even in the ethical section (Rom. 14.4 and 10), Paul asks, ‘Who are you to pass judgments on servants of another?’ (v. 4).")]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm"
  }, [_vm._v("The crowning study of Paul’s rhetoric in Romans, however, comes in Jewett’s "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Romans")]), _vm._v(" (2007). His section covers 23 quarto pages. Rhetoric, he argues, aims to persuade, as Paul did. He refers to the ancient epistolary handbooks which provided a distinctive rhetorical analysis. He first discusses ‘invention’ (Latin "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("inventio")]), _vm._v("), which seeks the most appropriate means of persuasion. These usually provide an "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("exordium")]), _vm._v(" or prologue, a "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("narrative")]), _vm._v(" which provides the background, an "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("argument")]), _vm._v(", and a "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("peroration")]), _vm._v(" or epilogue."), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Discourses")]), _vm._v(" 1.18, 22 and 1.11, 33."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n33",
      "id": "f33"
    }
  }, [_vm._v("33")])]), _vm._v(" The argument often draws on pre-Pauline creeds, hymns, and citations from Scripture. All these feature in Romans. Romans 1.3–4, for example, reflects a pre-Pauline christological creed. Romans 3.30 uses the pre-Pauline formula ‘God is one’. Romans 4.24 employs a pre-Pauline formula concerning the resurrection of Christ; while 4.25 borrows the Suffering Servant language from Isaiah 53.")]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm"
  }, [_vm._v("Jewett selects Romans 2.1–16 as ‘a diatribe that sustains Paul’s doctrine of impartial judgment’ that overturns much group exceptionalism in the Graeco-Roman world."), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Discourses")]), _vm._v(" 1.18, 22 and 1.11, 33."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n34",
      "id": "f34"
    }
  }, [_vm._v("34")])]), _vm._v(" Paul introduces an imaginary interlocutor who is a self-congratulatory bigot, to evoke contempt for him from his audience. Two diatribes follow in 3.1–8 and in 3.9–21, with questions and answers. Another follows in 4.1–12, and Paul introduces three rhetorical questions in 5.15–16. In 6.1–14 ‘the diatribal form is clearer’. Paul resumes the diatribe form in 9.30—10.4 and 11.1–10; ‘God did not cast off his people for ever, did he?’ A final diatribe appears in 13.3b. This list constitutes ten or more examples of this rhetorical form, which consistently serves Paul’s argument.")]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm"
  }, [_vm._v("Jewett finally argues that Paul uses the formal argument of an "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("enthymeme")]), _vm._v(" (Greek "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("enthumēma")]), _vm._v("), with special reference to Moores."), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Discourses")]), _vm._v(" 1.18, 22 and 1.11, 33."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n35",
      "id": "f35"
    }
  }, [_vm._v("35")])]), _vm._v(" Both Jewett and Moores highlight Paul’s emphasis on persuasion. Moores argues, ‘Reader-response theories turn Paul’s ideas upside down.’"), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Discourses")]), _vm._v(" 1.18, 22 and 1.11, 33."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n36",
      "id": "f36"
    }
  }, [_vm._v("36")])]), _vm._v(" Stressing Paul’s use of indirect communication, Moores writes, ‘It is in the nature of enthymematic argument that it creates puzzles.’"), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Discourses")]), _vm._v(" 1.18, 22 and 1.11, 33."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n37",
      "id": "f37"
    }
  }, [_vm._v("37")])]), _vm._v(" Some of Moores’ arguments may verge on the speculative, but he argues convincingly that the superimposition of one ‘code’ onto another is ‘a pervasive feature of sign-production’."), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Discourses")]), _vm._v(" 1.18, 22 and 1.11, 33."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n38",
      "id": "f38"
    }
  }, [_vm._v("38")])]), _vm._v(" He moves beyond the classical rhetoric cited by H. D. Betz and the ‘New Rhetoric’ advocated by W. Wuellner.")]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm"
  }, [_vm._v("A number of recent books and research articles on rhetoric in Paul relate to epistles other than Romans. Especially well known is Margaret Mitchell and L. L. Welborn (1997) on 1 Corinthians."), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Discourses")]), _vm._v(" 1.18, 22 and 1.11, 33."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n39",
      "id": "f39"
    }
  }, [_vm._v("39")])]), _vm._v(" Hans Dieter Betz on Galatians (1979) remains a classic."), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Discourses")]), _vm._v(" 1.18, 22 and 1.11, 33."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n40",
      "id": "f40"
    }
  }, [_vm._v("40")])]), _vm._v(" Several articles address the rhetoric of Romans, including especially those by Wuellner and Dunn."), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Discourses")]), _vm._v(" 1.18, 22 and 1.11, 33."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n41",
      "id": "f41"
    }
  }, [_vm._v("41")])])]), _vm._v(" "), _c('p', {
    staticClass: "h1",
    attrs: {
      "id": "a3"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/Table_of_contents"
    }
  }, [_c('span', {
    staticClass: "epub-semibold-normal-normal"
  }, [_vm._v("Sociological or socio-scientific reading")])])]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm-first"
  }, [_vm._v("This strategy of interpretation has essential relevance to Romans. The method has been growing in influence since at least the 1960s and 1970s. Edwin Judge, an ancient historian of Macquarie University, Australia, has been writing articles on this since 1960 and 1980."), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Discourses")]), _vm._v(" 1.18, 22 and 1.11, 33."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n42",
      "id": "f42"
    }
  }, [_vm._v("42")])]), _vm._v(" The social groupings and social composition of the ancient church in Rome are deeply relevant to understanding Romans. A first question concerns the economic status of believers in Rome. Were they desperately poor, or reasonably well off and educated? Answers may largely depend on whether different groups of Christians represented both ends of the spectrum. A second question asks what light can be shed on the Jewish and Gentile constituency of the church. Which group predominated, if both were present? Paul was writing, among other aims, to argue that both Jewish Christians and Gentile Christians stood on an equal footing before God. They were to welcome each other in mutual respect (Rom. 15.7).")]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm"
  }, [_vm._v("First, on "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("economic status")]), _vm._v(" there is no clear consensus about the intellectual and cultural constituency of the Roman church. This is hardly surprising, since there may have been some five or more groups within the church. Arguing for the higher end of the social scale, Robinson offers the common-sense answer: ‘Certainly some intelligence would have been needed to cope with the Epistle to the Romans.’"), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Discourses")]), _vm._v(" 1.18, 22 and 1.11, 33."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n43",
      "id": "f43"
    }
  }, [_vm._v("43")])]), _vm._v(" Similarly, Bruce observes, ‘Christianity may already have begun to make some impact in the higher reaches of Roman society.’"), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Discourses")]), _vm._v(" 1.18, 22 and 1.11, 33."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n44",
      "id": "f44"
    }
  }, [_vm._v("44")])]), _vm._v(" He cites, for example, the involvement with Christians and probably the Christian faith of Pomponia Graecina, the wife of Aulus Plautius, who commanded the military expedition to Britain in "), _c('span', {
    staticClass: "small"
  }, [_vm._v("AD")]), _vm._v(" 43. Following J. B. Lightfoot, Robinson accepts that the congregation was probably largely made up of freedmen and slaves, but probably ‘the better sort of Greek and some oriental slaves’, who would be more highly educated than their masters.")]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm"
  }, [_vm._v("Sanday and Headlam cite Prisca and Aquila as business people. In Romans 16.10–11, Paul mentions ‘those of the family of Aristobulus’ and ‘my relative Herodian’. This seems to point to members or the staff of the best families. In Romans 16, ten of the names mentioned are Latin names; 18 are Greek names; and two are arguably Hebrew names. The Roman historian Tacitus described Pomponia Graecina as an eminent or notable woman (Latin "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("insignis femina")]), _vm._v(")."), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Discourses")]), _vm._v(" 1.18, 22 and 1.11, 33."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n45",
      "id": "f45"
    }
  }, [_vm._v("45")])]), _vm._v(" Although the later years of Nero’s reign witnessed numerous crimes, his earlier reign from 54 to 59 was known as Nero’s prosperous and peaceful quinquennium.")]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm"
  }, [_vm._v("On the other side Peter Lampe contributed a landmark study of Christians in ancient Rome, in which he states that in the first century: ‘Poverty was such that some Roman Christians sold themselves into slavery to raise money for the poor in the church.’"), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Discourses")]), _vm._v(" 1.18, 22 and 1.11, 33."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n46",
      "id": "f46"
    }
  }, [_vm._v("46")])]), _vm._v(" Only after the 90s do we hear of ‘wealthy’ Christians. Lampe argues that in early years lower social strata predominated. Most Christians, he concluded, lived outside Rome on the western edge in the Valley of the Appian Way and the Tiber, or in Trastevere."), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Discourses")]), _vm._v(" 1.18, 22 and 1.11, 33."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n47",
      "id": "f47"
    }
  }, [_vm._v("47")])]), _vm._v(" A number of writers, including Jewett, speak of tenements in these areas. As in Corinth, the general picture may be ‘not many … powerful’ (NRSV), namely ‘influential people’ (1 Cor. 1.26), although there were also clearly specific exceptions to this.")]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm"
  }, [_vm._v("Jewett argues,")]), _vm._v(" "), _c('p', {
    staticClass: "ext-only"
  }, [_vm._v("Most of the population in Rome lived in the upper levels of ‘multi-story tenement houses’ or in the rears of shops, while the nobility and the wealthy citizens of Rome occupied the lower floors of these buildings or in mansions in the better parts of the city."), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Discourses")]), _vm._v(" 1.18, 22 and 1.11, 33."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n48",
      "id": "f48"
    }
  }, [_vm._v("48")])])]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm-first"
  }, [_vm._v("Crowding was intense for slaves or poorer people. Many tenements had four or five storeys, and consisted of apartment blocks. Jewett argues that most rooms were small (about 10 square metres), and the population density was ‘almost two and a half times higher than modern Calcutta and three times higher than Manhattan Island’."), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Discourses")]), _vm._v(" 1.18, 22 and 1.11, 33."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n49",
      "id": "f49"
    }
  }, [_vm._v("49")])]), _vm._v(" Spaces were often rented out by landlords or managers, and long-term tenure and rights of privacy were rare.")]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm"
  }, [_vm._v("While Roman citizens and the reasonably wealthy were greatly concerned with ‘honour and shame’, which Paul often calls ‘boasting’, slaves and the poor were generally beneath such considerations. Jewett comments,‘Largely exempt from the system of public honour were slaves and barbarians.’"), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Discourses")]), _vm._v(" 1.18, 22 and 1.11, 33."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n50",
      "id": "f50"
    }
  }, [_vm._v("50")])]), _vm._v(" Slaves were more numerous than we might imagine. Jeffers reports that slaves constituted ‘something like 2 to 3 million of the 7.5 million inhabitants of Italy’."), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Discourses")]), _vm._v(" 1.18, 22 and 1.11, 33."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n51",
      "id": "f51"
    }
  }, [_vm._v("51")])]), _vm._v(" Slaves originated from those captured in war, the children of slaves, those who chose to sell themselves into slavery for financial reasons, and those criminals for whom the penalty of their crime was slavery. As we shall note, some slaves were seen as mere ‘possessions’or‘things’, with neither freedom nor power, while others might achieve high-status work as managers of estates, secretaries and so forth. Martin has done much to show the range and complexity of the social status of slaves, which depended on the character of their‘lord’."), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Discourses")]), _vm._v(" 1.18, 22 and 1.11, 33."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n52",
      "id": "f52"
    }
  }, [_vm._v("52")])])]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm"
  }, [_vm._v("Jewett and Dunn, however, argue convincingly that there was more than one Christian community in Rome, and probably a very wide mix of social status. Jewett argues that Paul needed to rely on well-educated, Latin-speaking Romans to spearhead his hoped-for mission to Spain. Jewett works out a fascinating and convincing reconstruction, which we discuss below. The deacon Phoebe was eminent, he argues, and she would probably oversee this mission.")]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm"
  }, [_vm._v("Our second question is about the church’s composition of "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Jews and Christian Gentiles")]), _vm._v(". Every key theme or section in Romans reflects the composition of the church in Rome in this respect. Romans 9—11 and 12—15 speak as much to the church in Rome as the more classic chapters 1—8. Some have suggested that the Christians in Rome formed five separate groups of Christian believers, whom Paul sought to bring together as "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("one united church")]), _vm._v(". As James Dunn comments,‘the letter seems to be … dominated by the issue of Jew/Gentile relationships’; signalled by such an example as, ‘To the Jew first and also to the Greek’ (1.16)."), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Discourses")]), _vm._v(" 1.18, 22 and 1.11, 33."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n53",
      "id": "f53"
    }
  }, [_vm._v("53")])])]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm"
  }, [_vm._v("Although some scholars, including Lietzmann and O’Neill, have argued that the readers of the epistle were "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("all")]), _vm._v(" Jewish Christians, the  majority of writers rightly reject this conclusion. But Fitzmyer observes that Paul deliberately calls himself ‘Apostle to the Gentiles’ (Rom. 11.13) and even addresses his readers as one with other Gentiles (Rom. 1.13). By the 50s the Christians in Rome would have been well established. Paul had hoped to visit them ‘for many years’ (Rom. 1.10; 15.23). But, as Dunn observes, ‘Paul never speaks of the Christians in Rome as a church’."), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Discourses")]), _vm._v(" 1.18, 22 and 1.11, 33."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n54",
      "id": "f54"
    }
  }, [_vm._v("54")])]), _vm._v(" This is in marked contrast to his usual practice (1 Cor. 1.2; 2 Cor. 1.1; Phil. 4.15; 1 Thess. 1.2). Almost certainly they were too numerous to meet in a single house, and shared, in Dunn’s words, ‘the same sort of fragmented existence as the wider Jewish community … The Christians functioned as several “churches” in Rome, but were not seen as a single entity.’"), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Discourses")]), _vm._v(" 1.18, 22 and 1.11, 33."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n55",
      "id": "f55"
    }
  }, [_vm._v("55")])])]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm"
  }, [_vm._v("Fragmentation would have been further complicated when Jews returned to Rome after the death of Claudius. Fitzmyer rightly distinguishes controversies between Jewish Christians and Gentile Christians in Rome from that of Judaizers in Galatia."), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Discourses")]), _vm._v(" 1.18, 22 and 1.11, 33."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n56",
      "id": "f56"
    }
  }, [_vm._v("56")])]), _vm._v(" He concludes,")]), _vm._v(" "), _c('p', {
    staticClass: "ext-only"
  }, [_vm._v("When Paul writes to the Romans about justification by grace through faith, he is reflecting on his missionary endeavours of an earlier time … It is far from certain that charges levelled against Paul in Asia Minor and Greece made their way to Rome … [He] sounds … different from the tone in which he wrote Galatians."), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Discourses")]), _vm._v(" 1.18, 22 and 1.11, 33."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n57",
      "id": "f57"
    }
  }, [_vm._v("57")])])]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm-first"
  }, [_vm._v("Cranfield expresses the same thought differently. He writes,")]), _vm._v(" "), _c('p', {
    staticClass: "ext-only"
  }, [_vm._v("It would appear that there were a number of different groups. Did these groups meet on their own when they met for worship? Did they ever meet together as a single ‘church of God’ … "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("ekklēsia")]), _vm._v(" is never used in Romans … We ought to reckon with the fact that there may have been little, if any, central organisation."), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Discourses")]), _vm._v(" 1.18, 22 and 1.11, 33."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n58",
      "id": "f58"
    }
  }, [_vm._v("58")])])]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm-first"
  }, [_vm._v("Cranfield urges caution about this conclusion. Nevertheless it would explain further the clear theological links with 1 Corinthians, and Paul’s deep concern for mutual welcome and mutual tolerance between different Christians.")]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm"
  }, [_vm._v("Jews constituted a massive population in Rome. Some have calculated a number between 15,000 and 60,000 in the city in the 50s. Some occupied poorer quarters such as Trastevere, at least according to Philo."), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Discourses")]), _vm._v(" 1.18, 22 and 1.11, 33."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n59",
      "id": "f59"
    }
  }, [_vm._v("59")])]), _vm._v(" Others, including those captured by Pompey in 63 "), _c('span', {
    staticClass: "small"
  }, [_vm._v("BC")]), _vm._v(", became Roman citizens. Leon cites evidence for the poverty of some from their graves."), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Discourses")]), _vm._v(" 1.18, 22 and 1.11, 33."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n60",
      "id": "f60"
    }
  }, [_vm._v("60")])]), _vm._v(" The result of this quantity and variety emerged in the shape of several synagogues in Rome. One group was probably located in Trastevere; another in a district of labourers and small shopkeepers to the east of the Forum.")]), _vm._v(" "), _c('p', {
    staticClass: "chap-bm"
  }, [_vm._v("It is generally agreed that initially the church in Rome was composed largely of converted Jews, and that these grew alongside Gentile adherents until the expulsion of many Jews from Rome by Claudius. Then, when Paul wrote, there was a problematic relation between a Gentile majority and a Jewish minority. Many, if not most, identify these two groups with the ‘weak’ and the ‘strong’ in Romans 14.1—15.13, although many scholars are reluctant to define these too closely. Jewett argues that Paul’s characterization of each group is rhetorically exaggerated. Thus the strong‘eat everything’, while the weak eat only ‘leafy vegetables’ (Rom. 14.2)."), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Discourses")]), _vm._v(" 1.18, 22 and 1.11, 33."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#n61",
      "id": "f61"
    }
  }, [_vm._v("61")])]), _vm._v(" Kosher regulations would not in practice be so sweeping. But the general point is clear. The ‘weak’ would judge the ‘strong’ for not living up to kosher standards of the law; the ‘strong’ would judge the ‘weak’ for lacking Christian freedom from the law. Paul constantly strives to show that in God’s eyes Jewish Christians and Gentile Christians are equal. Each should welcome and respect the other (Rom. 15.1–13, especially v. 7).")]), _vm._v(" "), _c('p', {
    staticClass: "footnote-line"
  }, [_vm._v("________________")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f1",
      "id": "n1"
    }
  }, [_vm._v("1")])]), _vm._v("  Donfried, Karl P.,‘Introduction’ to Donfried, Karl P. (ed.), "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("The Romans Debate")]), _vm._v(" (Grand Rapids: Baker Academic, 2nd edn, 1991), p. xlvii.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f2",
      "id": "n2"
    }
  }, [_vm._v("2")])]), _vm._v("  Wagner, Günther, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Pauline Baptism and the Pagan Mysteries: The Problem of the Pauline Doctrine of Baptism in Romans VI:1–11, in the Light of Its Religio-Historical ‘Parallels’")]), _vm._v(" (Edinburgh: Oliver & Boyd, 1967).")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f3",
      "id": "n3"
    }
  }, [_vm._v("3")])]), _vm._v("  Davies, W. D., "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Paul and Rabbinic Judaism")]), _vm._v(" (London: SPCK, 2nd edn, 1955), pp. 20–31.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f4",
      "id": "n4"
    }
  }, [_vm._v("4")])]), _vm._v("  Davies, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Paul")]), _vm._v(", pp. 31–5.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f5",
      "id": "n5"
    }
  }, [_vm._v("5")])]), _vm._v("  Davies, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Paul")]), _vm._v(", p. 57.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f6",
      "id": "n6"
    }
  }, [_vm._v("6")])]), _vm._v("  Dunn, James D. G.,‘The New Perspective on Paul: Paul and the Law’ in Donfried, Karl P. (ed.), "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("The Romans Debate")]), _vm._v(" (Grand Rapids: Baker Academic, 2nd edn, 1991), p. 299; cf. pp. 299–308.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f7",
      "id": "n7"
    }
  }, [_vm._v("7")])]), _vm._v("  Dunn,‘New Perspective’, p. 301.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f8",
      "id": "n8"
    }
  }, [_vm._v("8")])]), _vm._v("  Dunn,‘New Perspective’, p. 304.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f9",
      "id": "n9"
    }
  }, [_vm._v("9")])]), _vm._v("  Dunn,‘New Perspective’, p. 307.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f10",
      "id": "n10"
    }
  }, [_vm._v("10")])]), _vm._v("  Wright, N. T., "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("The Messiah and the People of God")]), _vm._v(" (Oxford: OUP, 1980), esp. ch. 2.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f11",
      "id": "n11"
    }
  }, [_vm._v("11")])]), _vm._v("  Wright, N. T., "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Paul: Fresh Perspectives")]), _vm._v(" (London: SPCK, 2005), p. 117.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f12",
      "id": "n12"
    }
  }, [_vm._v("12")])]), _vm._v("  Wright, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Fresh Perspectives")]), _vm._v(", p. 117.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f13",
      "id": "n13"
    }
  }, [_vm._v("13")])]), _vm._v("  Wright, Tom, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("What St. Paul Really Said")]), _vm._v(" (Oxford: Lion, 1997), p. 115; quotation from McGrath, Alister E., "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Iustitia Dei: A History of the Christian Doctrine of Justification")]), _vm._v(" (2 vols, Cambridge: CUP, 1986), vol. 1, pp. 2–3.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f14",
      "id": "n14"
    }
  }, [_vm._v("14")])]), _vm._v("  Blackwell, Ben C., and others (eds), "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Reading Romans in Context: Paul and Second Temple Judaism")]), _vm._v(" (Grand Rapids: Zondervan, 2015).")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f15",
      "id": "n15"
    }
  }, [_vm._v("15")])]), _vm._v("  Jewett, Robert, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Romans: A Commentary")]), _vm._v(" (Minneapolis: Fortress, 2007; Hermeneia), p. 1 (my italics).")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f16",
      "id": "n16"
    }
  }, [_vm._v("16")])]), _vm._v("  Watson, Duane F., and Hauser, Alan J., "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Rhetorical Criticism and the Bible")]), _vm._v(" (Leiden: Brill, 1994; BI 4), pp. 9–12.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f17",
      "id": "n17"
    }
  }, [_vm._v("17")])]), _vm._v("  Bultmann, Rudolf, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Der Stil der paulinischen Predigt und die kynisch-stoische Diatribe")]), _vm._v(" (Göttingen: Vandenhoeck & Ruprecht, 1910; FRLANT 13).")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f18",
      "id": "n18"
    }
  }, [_vm._v("18")])]), _vm._v("  Bultmann, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Der Stil")]), _vm._v(", p. 109 and throughout.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f19",
      "id": "n19"
    }
  }, [_vm._v("19")])]), _vm._v("  Bultmann, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Der Stil")]), _vm._v(", pp. 9–10.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f20",
      "id": "n20"
    }
  }, [_vm._v("20")])]), _vm._v("  Bultmann, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Der Stil")]), _vm._v(", p. 65.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f21",
      "id": "n21"
    }
  }, [_vm._v("21")])]), _vm._v("  Bultmann, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Der Stil")]), _vm._v(", pp. 67–8.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f22",
      "id": "n22"
    }
  }, [_vm._v("22")])]), _vm._v("  Bultmann, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Der Stil")]), _vm._v(", p. 70.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f23",
      "id": "n23"
    }
  }, [_vm._v("23")])]), _vm._v("  Bultmann, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Der Stil")]), _vm._v(", pp. 71–4.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f24",
      "id": "n24"
    }
  }, [_vm._v("24")])]), _vm._v("  Bultmann, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Der Stil")]), _vm._v(", pp. 74–88.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f25",
      "id": "n25"
    }
  }, [_vm._v("25")])]), _vm._v("  Bultmann, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Der Stil")]), _vm._v(", p. 96.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f26",
      "id": "n26"
    }
  }, [_vm._v("26")])]), _vm._v("  Bultmann, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Der Stil")]), _vm._v(", p. 108.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f27",
      "id": "n27"
    }
  }, [_vm._v("27")])]), _vm._v("  Stowers, Stanley K., "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("The Diatribe and Paul’s Letter to the Romans")]), _vm._v(" (Chico: Scholars Press, 1981; SBLDS 57).")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f28",
      "id": "n28"
    }
  }, [_vm._v("28")])]), _vm._v("  Dobschütz, E. von, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Der Apostel Paulus")]), _vm._v(" (Halle: Waisenhaus, 1926), pp. 122–3; and Stowers, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("The Diatribe and Romans")]), _vm._v(", p. 40.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f29",
      "id": "n29"
    }
  }, [_vm._v("29")])]), _vm._v("  Judge, Edwin, A., ‘St Paul and Classical Society’, JAC 15 (1972), p. 33.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f30",
      "id": "n30"
    }
  }, [_vm._v("30")])]), _vm._v("  Stowers, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("The Diatribe and Romans")]), _vm._v(", p. 55.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f31",
      "id": "n31"
    }
  }, [_vm._v("31")])]), _vm._v("  Stowers, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("The Diatribe and Romans")]), _vm._v(", p. 110.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f32",
      "id": "n32"
    }
  }, [_vm._v("32")])]), _vm._v("  Stowers, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("The Diatribe and Romans")]), _vm._v(", p. 112.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f33",
      "id": "n33"
    }
  }, [_vm._v("33")])]), _vm._v("  Jewett, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Romans")]), _vm._v(", p. 24.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f34",
      "id": "n34"
    }
  }, [_vm._v("34")])]), _vm._v("  Jewett, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Romans")]), _vm._v(", p. 26.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f35",
      "id": "n35"
    }
  }, [_vm._v("35")])]), _vm._v("  Moores, John D., "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Wrestling with Rationality in Paul: Romans 1—8 in a New Perspective")]), _vm._v(" (Cambridge: CUP, 1995; SNTSMS 82).")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f36",
      "id": "n36"
    }
  }, [_vm._v("36")])]), _vm._v("  Moores, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Wrestling with Rationality")]), _vm._v(", pp. 133–4.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f37",
      "id": "n37"
    }
  }, [_vm._v("37")])]), _vm._v("  Moores, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Wrestling with Rationality")]), _vm._v(", p. 60.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f38",
      "id": "n38"
    }
  }, [_vm._v("38")])]), _vm._v("  Moores, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Wrestling with Rationality")]), _vm._v(", p. 26.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f39",
      "id": "n39"
    }
  }, [_vm._v("39")])]), _vm._v("  Welborn, L. L., "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Politics and Rhetoric in the Corinthian Epistles")]), _vm._v(" (Macon, GA: Mercer University Press, 1997).")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f40",
      "id": "n40"
    }
  }, [_vm._v("40")])]), _vm._v("  Betz, H. D., "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Galatians")]), _vm._v(" (Philadelphia: Fortress, 1979; Hermeneia).")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f41",
      "id": "n41"
    }
  }, [_vm._v("41")])]), _vm._v("  Dunn, J. D. G., ‘The Formal and Theological Coherence of Romans’ in Donfried, Karl P. (ed.), "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("The Romans Debate")]), _vm._v(" (Grand Rapids: Baker Academic, 2nd edn, 1991), pp. 245–50; and Wuellner, W., ‘Paul’s Rhetoric of Argumentation in Romans’ in Donfried (ed.), "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Romans Debate")]), _vm._v(", pp. 128–46.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f42",
      "id": "n42"
    }
  }, [_vm._v("42")])]), _vm._v("  Judge, Edwin A., "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("The Social Pattern of the Christian Groups in the First Century")]), _vm._v(" (London: Tyndale, 1960); and ‘The Social Identity of the First Christians: A Question of Method in Religious History’, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("JRH")]), _vm._v(" 11.2 (1980), pp. 201–17.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f43",
      "id": "n43"
    }
  }, [_vm._v("43")])]), _vm._v("  Robinson, J. A. T., "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Wrestling with Romans")]), _vm._v(" (London: SCM, 1979), p. 7.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f44",
      "id": "n44"
    }
  }, [_vm._v("44")])]), _vm._v("  Bruce, F. F., "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("The Epistle of Paul to the Romans")]), _vm._v(" (London: Tyndale, 1963), p. 16.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f45",
      "id": "n45"
    }
  }, [_vm._v("45")])]), _vm._v("  Tacitus, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Annals")]), _vm._v(", 13.32.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f46",
      "id": "n46"
    }
  }, [_vm._v("46")])]), _vm._v("  Lampe, Peter, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Die stadtrömischen Christen in den ersten beiden Jahrhunderten")]), _vm._v(" (Tübingen: Mohr, 1987; WUNT II.18), esp. pp. 68–9; Eng., "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("From Paul to Valentinus: Christians at Rome in the First Two Centuries")]), _vm._v(" (Minneapolis: Fortress, 2003).")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f47",
      "id": "n47"
    }
  }, [_vm._v("47")])]), _vm._v("  Lampe, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Die stadtrömischen Christen")]), _vm._v(", pp. 41–9.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f48",
      "id": "n48"
    }
  }, [_vm._v("48")])]), _vm._v("  Jewett, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Romans")]), _vm._v(", p. 53; and Jeffers, James S., ‘Jewish and Christian Families in First-Century Rome’ in Donfried, K. P., and Richardson, P., "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Judaism and Christianity in First-Century Rome")]), _vm._v(" (Grand Rapids: Eerdmans, 1998), pp. 128–50.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f49",
      "id": "n49"
    }
  }, [_vm._v("49")])]), _vm._v("  Jewett, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Romans")]), _vm._v(", p. 54.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f50",
      "id": "n50"
    }
  }, [_vm._v("50")])]), _vm._v("  Jewett, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Romans")]), _vm._v(", p. 51.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f51",
      "id": "n51"
    }
  }, [_vm._v("51")])]), _vm._v("  Jeffers, ‘Jewish and Christian Families’, p. 221.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f52",
      "id": "n52"
    }
  }, [_vm._v("52")])]), _vm._v("  Martin, Dale B., "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Slavery as Salvation")]), _vm._v(" (New Haven: Yale University Press, 1990).")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f53",
      "id": "n53"
    }
  }, [_vm._v("53")])]), _vm._v("  Dunn, James D. G., "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Romans 1—8")]), _vm._v(" (Dallas: Word, 1988; WBC 38), p. xlv.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f54",
      "id": "n54"
    }
  }, [_vm._v("54")])]), _vm._v("  Dunn, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Romans 1—8")]), _vm._v(", p. lii.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f55",
      "id": "n55"
    }
  }, [_vm._v("55")])]), _vm._v("  Dunn, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Romans 1—8")]), _vm._v(", p. lii.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f56",
      "id": "n56"
    }
  }, [_vm._v("56")])]), _vm._v("  Fitzmyer, Joseph A., "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Romans: A New Translation with Introduction and Commentary")]), _vm._v(" (New York: Doubleday, 1992; AB 33), p. 34.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f57",
      "id": "n57"
    }
  }, [_vm._v("57")])]), _vm._v("  Fitzmyer, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Romans")]), _vm._v(", p. 34.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f58",
      "id": "n58"
    }
  }, [_vm._v("58")])]), _vm._v("  Cranfield, C. E. B., "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("A Critical and Exegetical Commentary on the Epistle to the Romans")]), _vm._v(", vol. 1 (Edinburgh: T & T Clark, 1975; ICC), p. 22.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f59",
      "id": "n59"
    }
  }, [_vm._v("59")])]), _vm._v("  Philo, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Legatio")]), _vm._v(", 155–6.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f60",
      "id": "n60"
    }
  }, [_vm._v("60")])]), _vm._v("  Leon, H. J., "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("The Jews of Ancient Rome")]), _vm._v(" (Peabody: Hendrickson, 1995), pp. 122–4.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('sup', [_c('a', {
    attrs: {
      "href": "#/#f61",
      "id": "n61"
    }
  }, [_vm._v("61")])]), _vm._v("  Jewett, "), _c('span', {
    staticClass: "italics"
  }, [_vm._v("Romans")]), _vm._v(", p. 71.")])])])])
}]}


//////////////////
// WEBPACK FOOTER
// ../node_modules/vue-loader/lib/template-compiler?{"id":"data-v-5b506226","hasScoped":false,"transformToRequire":{"video":"src","source":"src","img":"src","image":"xlink:href"}}!../node_modules/vue-loader/lib/selector.js?type=template&index=0!./src/components/Chapter02.vue
// module id = 81
// module chunks = 0